import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard, WizardNavigateCallback } from '@/_shared/hooks/useWizard';
import {
  getEmployeesImportsOverviewCall,
  getEmployeesImportsStatsCall,
  postEmployeesImportsAbortCall,
  postEmployeesImportsConfirmCall,
  postEmployeesImportsTemplateCall,
} from '@/people/state/api';
import { CardsIncompleteData } from './CardsIncompleteData';
import {
  DialogConfirmNavigateBack,
  DialogConfirmNavigateBackPayload,
} from './DialogConfirmNavigateBack';
import { DialogEditImportedProfiles } from './DialogEditImportedProfiles';
import { DialogEditImportedProfilesPayload } from './DialogEditImportedProfiles/types';

type Props = { wizardNavigationButton: WizardNavigationButton };

export const VerifyData: FC<Props> = ({ wizardNavigationButton }) => {
  const t = useTranslations('configuration.hr.verifyData');
  const sharedT = useTranslations();
  const { openDialog } = useDialog<DialogConfirmNavigateBackPayload>(
    'uploadCompanyDataConfirmNavigateBack',
  );
  const { openDialog: openDialogEdit } = useDialog<DialogEditImportedProfilesPayload>(
    'uploadCompanyDataEditImportedProfiles',
  );
  const {
    setCustomOnPrevious,
    setCustomOnNext,
    setCustomOnSkip,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const gapS = convertSizeToNumber(`${useThemeValue('sizes', 's')}`);
  const gapXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxl')}`);
  const { genericResponsiveValue } = useResponsiveStyle();
  const { notify } = useNotification();
  const {
    total,
    completed,
    invalid,
    issuesList,
    loading: loadingGetEmployeesImportsStats,
  } = getEmployeesImportsStatsCall.hook();
  const { execute: executePostEmployeesImportsConfirm } = postEmployeesImportsConfirmCall.hook();
  const { jobId } = postEmployeesImportsTemplateCall.hook();
  const { execute: executePostEmployeesImportsAbort } = postEmployeesImportsAbortCall.hook();
  const { reset: resetGetEmployeesImportsOverview } = getEmployeesImportsOverviewCall.hook();

  const {
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm();
  const fieldValues = watch();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues).some((key: string) => !!fieldValues[key]);
  }, [fieldValues]);

  const onSubmit = useCallback(
    (navigate?: WizardNavigateCallback) => {
      if (jobId) {
        executePostEmployeesImportsConfirm({
          jobId,
          successSaga: () => {
            navigate?.();
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePostEmployeesImportsConfirm, jobId, sharedT],
  );

  const onSkip: NonNullable<Parameters<typeof setCustomOnSkip>[0]> = useCallback(() => {
    openDialogEdit();
  }, [openDialogEdit]);

  const onAbortImport = useCallback(
    () => {
      if (jobId) {
        executePostEmployeesImportsAbort({
          jobId,
          successSaga: () => {
            resetGetEmployeesImportsOverview();
          },
          errorSaga: () => {
            notify(sharedT('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePostEmployeesImportsAbort, resetGetEmployeesImportsOverview, sharedT, t, jobId],
  );

  const onPrevious: NonNullable<Parameters<typeof setCustomOnPrevious>[0]> = useCallback(
    navigate => {
      openDialog({ navigate, abort: onAbortImport });
    },
    [openDialog, onAbortImport],
  );

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnSkip(onSkip);
    setCustomOnPrevious(onPrevious);

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [
    setCustomOnPrevious,
    handleSubmit,
    onSkip,
    onSubmit,
    resetWizardNavigationCallbacks,
    setCustomOnNext,
    setCustomOnSkip,
    onPrevious,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: false,
      isLoadingNextButton: false,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [isValid, resetWizardNavigationButtonStatus, setNavigationButtonStatus, someInputFilled]);

  return (
    <Box mb='s'>
      <Box
        display='flex'
        flex={10}
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap: gapXXL }}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='left'
          alignItems='left'
          textAlign='left'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 6 },
            defaultValue: 10,
          })}
        >
          <Box mb='m'>
            <Typography
              variant='h1'
              color='primary/text'
              fontSize={genericResponsiveValue({
                breakPoints: { desktop: 50 as never },
                defaultValue: 'xxxl',
              })}
              lineHeight={genericResponsiveValue({
                breakPoints: { desktop: '60px' as never },
                defaultValue: 'xxxl',
              })}
              fontWeight='bold'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'm' },
                defaultValue: 'xs',
              })}
            >
              {t('title')}
            </Typography>
            <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
              {t('subtitle')}
            </Typography>
          </Box>
          <Box mt='xs'>
            <Box display='flex' style={{ gap: gapXS }}>
              <wizardNavigationButton.Next />
              <wizardNavigationButton.Skip />
            </Box>
          </Box>
        </Box>
        <Box
          display='flex'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 4 },
            defaultValue: 10,
          })}
          style={{ gap: gapXS }}
          flexDirection='row'
          flexWrap='wrap'
          width='100%'
        >
          <Box width='100%'>
            <Card height='100%' style={{ boxSizing: 'border-box' }} borderRadius='m'>
              <Box display='flex' flexDirection='column'>
                <Box display='flex'>
                  <Typography variant='h3' pb='xxs'>
                    {t('charts.totalProfiles')}
                  </Typography>
                </Box>
                <Box>
                  <Charts.HalfPieChartComponent
                    colors={['primary', 'white']}
                    data={[
                      {
                        name: t('charts.completeProfiles'),
                        value: completed,
                      },
                      { name: t('charts.incompleteProfiles'), value: invalid },
                    ]}
                    centerValue={total}
                    centerLabelValue={t('charts.profilesLabel')}
                  />
                </Box>
              </Box>
            </Card>
          </Box>
          <Box width='100%'>
            {issuesList?.length ? (
              <Card height='100%' style={{ boxSizing: 'border-box' }} borderRadius='m' mt='s'>
                <Box display='flex' flexDirection='column'>
                  <Box display='flex' mb='s'>
                    <Typography variant='h3'>{t('previewDataWarning.title')}</Typography>
                  </Box>
                  <Box
                    style={{ gap: gapS }}
                    display='grid'
                    gridTemplateColumns={`repeat(${1}, 1fr)`}
                  >
                    {loadingGetEmployeesImportsStats ? (
                      <CardsIncompleteData.Skeleton />
                    ) : (
                      issuesList.map((item, idx) => (
                        <Box key={item.code ?? idx} width='100%' flex={1}>
                          <CardsIncompleteData incompleteData={item} />
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Card>
            ) : null}
          </Box>
        </Box>
      </Box>
      <DialogConfirmNavigateBack />
      <DialogEditImportedProfiles />
    </Box>
  );
};
